import { Check, MoreHorizontal } from 'lucide-react'
import { useState } from 'react'
import { PopoverSheet } from '@/components/ui/PopoverSheet'
import { SegmentedControl, segmentClass, type Segment } from '@/components/ui/SegmentedControl'
import { cn } from '@/lib/utils'
import type { ScopePerson, ViewScope } from '@/lib/report-types'

interface PersonScopeControlProps {
  scope: ViewScope
  people: ScopePerson[]
  /** Wie de "persoonlijke" slot nu toont; undefined = jezelf. */
  personId?: string
  /** Wisselt Persoonlijk/Bedrijf; laat personId ongemoeid, die blijft vastzitten. */
  onScope: (scope: ViewScope) => void
  /** undefined = "Mijzelf". Kiest altijd ook scope=personal. */
  onPerson: (personId: string | undefined) => void
  /** Rustlabel van het eerste segment als je jezelf bekijkt, bv. "Mijzelf" of "Mijn deel". */
  personalLabel: string
  companyLabel: string
  /** Titel van de puntjes-lijst, bv. "Bekijk het dashboard van". */
  menuTitle: string
  /** Regel bovenaan de lijst om terug te wisselen naar jezelf. */
  selfLabel?: string
  /** Aria-label van de hele segmentengroep. */
  label: string
}

/**
 * Persoonlijk/Bedrijf, met een altijd-gelijk "···"-knopje ernaast om het eerste
 * segment op andermans cijfers te zetten. Het "···"-icoon verandert nooit en
 * doet nooit mee met de glijdende actieve-staat: dat is wat eerder liet zien
 * dat er nog een keuzelijst achter zat, ook nadat je al iemand had gekozen.
 * In plaats daarvan neemt de naam van de gekozen collega de plek van het
 * eerste segment in, en blijft dat zo tot je "Mijzelf" of iemand anders kiest,
 * ook als je ondertussen naar Bedrijf en terug schakelt.
 */
export function PersonScopeControl({
  scope,
  people,
  personId,
  onScope,
  onPerson,
  personalLabel,
  companyLabel,
  menuTitle,
  selfLabel = 'Mijzelf',
  label,
}: PersonScopeControlProps) {
  const [open, setOpen] = useState(false)

  const self = people.find((person) => person.isSelf)
  const others = people.filter((person) => !person.isSelf)
  const viewingSelf = personId === undefined || personId === self?.id
  const selected = viewingSelf ? undefined : others.find((person) => person.id === personId)

  const segments: Segment<ViewScope>[] = [
    { value: 'personal', label: viewingSelf ? personalLabel : (selected?.shortName ?? personalLabel) },
    { value: 'company', label: companyLabel },
  ]

  return (
    <SegmentedControl
      segments={segments}
      value={scope}
      onChange={onScope}
      label={label}
      trailing={
        others.length > 0 ? (
          <PopoverSheet
            open={open}
            onOpenChange={setOpen}
            title={menuTitle}
            align="end"
            className="w-56"
            trigger={
              <button type="button" aria-label={menuTitle} className={segmentClass(false)}>
                <MoreHorizontal className="h-4 w-4" />
              </button>
            }
          >
            <div className="flex flex-col p-1.5">
              <button
                type="button"
                onClick={() => {
                  onPerson(undefined)
                  setOpen(false)
                }}
                aria-current={viewingSelf ? 'true' : undefined}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-md px-2.5 py-2 text-left text-sm transition-colors duration-150 hover:bg-fill-2',
                  viewingSelf ? 'font-medium text-ink' : 'text-ink-soft',
                )}
              >
                {selfLabel}
                {viewingSelf ? <Check className="h-3.5 w-3.5" /> : null}
              </button>
              <div className="my-1 border-t border-hairline" />
              {others.map((person) => (
                <button
                  key={person.id}
                  type="button"
                  onClick={() => {
                    onPerson(person.id)
                    setOpen(false)
                  }}
                  aria-current={person.id === selected?.id ? 'true' : undefined}
                  className={cn(
                    'flex items-center justify-between gap-3 rounded-md px-2.5 py-2 text-left text-sm transition-colors duration-150 hover:bg-fill-2',
                    person.id === selected?.id ? 'font-medium text-ink' : 'text-ink-soft',
                  )}
                >
                  {person.displayName}
                  {person.id === selected?.id ? <Check className="h-3.5 w-3.5" /> : null}
                </button>
              ))}
            </div>
          </PopoverSheet>
        ) : null
      }
    />
  )
}
